/**
 * AircraftDetailPage - Phase X4
 * Shows aircraft details including maintenance tab
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { isMaxSpeedMissing } from '../lib/aircraftMaxSpeed';
import { isMtomMissing } from '../lib/aircraftMtom';
import dayjs from 'dayjs';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { 
  Plane, ArrowLeft, Wrench, Calendar, Plus, Pencil, X, 
  AlertTriangle, CheckCircle, Clock, XCircle, Loader2, Shield
} from 'lucide-react';
import { toast } from 'sonner';

import { MaintenanceScheduler } from '../components/aircraft/MaintenanceScheduler';

// Wave 3 Block 3A: Aircraft Insurance Section
const AircraftInsuranceSection = ({ aircraftId, language }) => {
  const [loading, setLoading] = useState(true);
  const [insuranceStatus, setInsuranceStatus] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchInsurance = async () => {
      try {
        const response = await api.get(`/insurance/aircraft/${aircraftId}`);
        setInsuranceStatus(response.data);
      } catch (err) {
        console.error('Failed to fetch aircraft insurance:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInsurance();
  }, [aircraftId]);
  
  const t = {
    title: language === 'cs' ? 'Pojištění letadla' : 'Aircraft Insurance',
    noInsurance: language === 'cs' ? 'Žádné aktivní pojištění' : 'No active insurance',
    addInsurance: language === 'cs' ? 'Přidat pojištění' : 'Add Insurance',
    policy: language === 'cs' ? 'Pojistka' : 'Policy',
    provider: language === 'cs' ? 'Pojišťovna' : 'Provider',
    coverage: language === 'cs' ? 'Krytí' : 'Coverage',
    validUntil: language === 'cs' ? 'Platnost do' : 'Valid Until',
    daysRemaining: language === 'cs' ? 'Zbývá dní' : 'Days remaining',
    warnings: language === 'cs' ? 'Varování' : 'Warnings'
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="p-6 text-center text-red-600">
          {language === 'cs' ? 'Chyba při načítání pojištění' : 'Error loading insurance'}
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          {t.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!insuranceStatus?.has_insurance ? (
          <div className="text-center py-8">
            <Shield className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">{t.noInsurance}</p>
            <Button variant="outline" onClick={() => window.location.href = '/insurance'}>
              <Plus className="w-4 h-4 mr-2" />
              {t.addInsurance}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Warnings */}
            {insuranceStatus.warnings?.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <ul className="text-sm text-amber-700">
                    {insuranceStatus.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Policies */}
            {insuranceStatus.policies?.map((policy) => (
              <div key={policy.id} className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{policy.provider}</span>
                  <Badge className={policy.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {policy.status === 'active' 
                      ? (language === 'cs' ? 'Aktivní' : 'Active')
                      : (language === 'cs' ? 'Neaktivní' : 'Inactive')}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">{t.policy}:</span>
                    <span className="ml-2">{policy.policy_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t.coverage}:</span>
                    <span className="ml-2">{policy.coverage_amount?.toLocaleString()} {policy.currency}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t.validUntil}:</span>
                    <span className="ml-2">
                      {policy.valid_until ? new Date(policy.valid_until).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US') : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t.daysRemaining}:</span>
                    <span className={`ml-2 ${policy.days_until_expiry < 30 ? 'text-amber-600 font-medium' : ''}`}>
                      {policy.days_until_expiry}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Maintenance type colors
const TYPE_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  unscheduled: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  calibration: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  repair: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  inspection: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
};

// Status colors
const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-500'
};

export const AircraftDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  const tr = t.aircraft?.maintenance || {};
  const trDetail = tr.detail || {};
  const types = tr.types || {};
  const statuses = tr.statuses || {};
  
  const [aircraft, setAircraft] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  
  // Maintenance scheduler modal
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  
  // Cancel confirmation dialog
  const [cancelDialog, setCancelDialog] = useState({ open: false, item: null });
  const [cancelling, setCancelling] = useState(false);
  
  // Check if aircraft is currently in maintenance
  const activeMaintenance = maintenance.find(m => {
    if (m.status !== 'scheduled' && m.status !== 'in_progress') return false;
    const now = dayjs();
    const start = dayjs(m.start_at);
    const end = dayjs(m.end_at);
    return now.isAfter(start) && now.isBefore(end);
  });
  
  const fetchAircraft = useCallback(async () => {
    try {
      const res = await api.get(`/aircraft/${id}`);
      setAircraft(res.data);
    } catch (err) {
      toast.error(language === 'cs' ? 'Nepodařilo se načíst dron' : 'Failed to load aircraft');
      navigate('/aircraft');
    }
  }, [id, language, navigate]);
  
  const fetchMaintenance = useCallback(async () => {
    try {
      const res = await api.get(`/aircraft/${id}/maintenance?lang=${language}&limit=50`);
      setMaintenance(res.data || []);
    } catch (err) {
      console.error('Failed to load maintenance:', err);
    }
  }, [id, language]);
  
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchAircraft(), fetchMaintenance()]);
      setLoading(false);
    };
    load();
  }, [fetchAircraft, fetchMaintenance]);
  
  const handleScheduleSuccess = () => {
    fetchMaintenance();
    setSchedulerOpen(false);
    setEditingMaintenance(null);
    toast.success(language === 'cs' ? 'Údržba uložena' : 'Maintenance saved');
  };
  
  const handleEdit = (item) => {
    setEditingMaintenance(item);
    setSchedulerOpen(true);
  };
  
  const handleCancelClick = (item) => {
    setCancelDialog({ open: true, item });
  };
  
  const handleConfirmCancel = async () => {
    if (!cancelDialog.item) return;
    
    setCancelling(true);
    try {
      await api.delete(`/aircraft/${id}/maintenance/${cancelDialog.item.id}?lang=${language}`);
      toast.success(language === 'cs' ? 'Údržba zrušena' : 'Maintenance cancelled');
      fetchMaintenance();
    } catch (err) {
      toast.error(language === 'cs' ? 'Nepodařilo se zrušit údržbu' : 'Failed to cancel maintenance');
    } finally {
      setCancelling(false);
      setCancelDialog({ open: false, item: null });
    }
  };
  
  // Group maintenance by status
  const upcomingMaintenance = maintenance.filter(m => m.status === 'scheduled' && dayjs(m.start_at).isAfter(dayjs()));
  const inProgressMaintenance = maintenance.filter(m => m.status === 'in_progress' || (m.status === 'scheduled' && dayjs(m.start_at).isBefore(dayjs()) && dayjs(m.end_at).isAfter(dayjs())));
  const historyMaintenance = maintenance.filter(m => m.status === 'completed' || m.status === 'cancelled').slice(0, 10);
  
  const canManageMaintenance = ['platform_admin', 'organization_admin', 'org_admin', 'compliance_manager'].includes(user?.role);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (!aircraft) {
    return null;
  }
  
  const formatDateTime = (dt) => {
    if (!dt) return '-';
    return dayjs(dt).format(language === 'cs' ? 'DD.MM.YYYY HH:mm' : 'YYYY-MM-DD HH:mm');
  };
  
  const MaintenanceRow = ({ item }) => (
    <TableRow data-testid={`maintenance-row-${item.id}`}>
      <TableCell className="font-medium">
        {formatDateTime(item.start_at)} - {formatDateTime(item.end_at)}
      </TableCell>
      <TableCell>
        <Badge className={TYPE_COLORS[item.type] || 'bg-gray-100'}>
          {types[item.type] || item.type}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge className={STATUS_COLORS[item.status] || 'bg-gray-100'}>
          {statuses[item.status] || item.status}
        </Badge>
      </TableCell>
      <TableCell className="max-w-[200px] truncate" title={item.reason}>
        {item.reason}
      </TableCell>
      <TableCell>{item.technician || '-'}</TableCell>
      <TableCell>
        {canManageMaintenance && item.status !== 'cancelled' && item.status !== 'completed' && (
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleEdit(item)}
              data-testid={`edit-maintenance-${item.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleCancelClick(item)}
              className="text-red-600 hover:text-red-700"
              data-testid={`cancel-maintenance-${item.id}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
  
  const EmptyState = () => (
    <div className="text-center py-12" data-testid="maintenance-empty-state">
      <Wrench className="h-12 w-12 mx-auto text-gray-400 mb-4" />
      <p className="text-gray-500 mb-4">
        {tr.noMaintenance || 'No maintenance scheduled yet.'}
      </p>
      {canManageMaintenance && (
        <Button onClick={() => setSchedulerOpen(true)} data-testid="schedule-first-maintenance">
          <Plus className="h-4 w-4 mr-2" />
          {tr.scheduleFirst || 'Schedule first maintenance'}
        </Button>
      )}
    </div>
  );
  
  return (
    <div className="space-y-6 p-6" data-testid="aircraft-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/aircraft')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Plane className="h-6 w-6 text-blue-600" />
              {aircraft.model}
            </h1>
            <p className="text-sm text-gray-500">
              {aircraft.registration_number || aircraft.serial_number}
            </p>
          </div>
        </div>
      </div>
      
      {/* Active maintenance banner */}
      {activeMaintenance && (
        <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-900/20" data-testid="active-maintenance-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-200 font-medium">
            {language === 'cs' 
              ? 'Tento dron je aktuálně v servisu.' 
              : 'This aircraft is currently in maintenance.'}
            <span className="ml-2 text-sm font-normal">
              ({formatDateTime(activeMaintenance.start_at)} - {formatDateTime(activeMaintenance.end_at)})
            </span>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">
            <Plane className="h-4 w-4 mr-2" />
            {language === 'cs' ? 'Informace' : 'Info'}
          </TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">
            <Wrench className="h-4 w-4 mr-2" />
            {tr.title || 'Maintenance'}
          </TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance">
            <Shield className="h-4 w-4 mr-2" />
            {language === 'cs' ? 'Pojištění' : 'Insurance'}
          </TabsTrigger>
        </TabsList>
        
        {/* Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardContent className="pt-6">
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Model' : 'Model'}</dt>
                  <dd className="font-medium">{aircraft.model}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Sériové číslo' : 'Serial Number'}</dt>
                  <dd className="font-medium">{aircraft.serial_number || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Registrace' : 'Registration'}</dt>
                  <dd className="font-medium">{aircraft.registration_number || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Letové hodiny' : 'Flight Hours'}</dt>
                  <dd className="font-medium">{aircraft.flight_hours || 0} h</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Poslední údržba' : 'Last Maintenance'}</dt>
                  <dd className="font-medium">
                    {aircraft.last_maintenance_date 
                      ? dayjs(aircraft.last_maintenance_date).format(language === 'cs' ? 'DD.MM.YYYY' : 'YYYY-MM-DD')
                      : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Interval servisu' : 'Service Interval'}</dt>
                  <dd className="font-medium">{aircraft.service_interval || '-'} h</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'MTOM' : 'MTOM'}</dt>
                  <dd className="font-medium">
                    {isMtomMissing(aircraft) ? (
                      <button
                        type="button"
                        className="text-left text-amber-600 hover:underline flex items-start gap-1"
                        title={t.fleet?.mtomMissingHint}
                        onClick={() => navigate(`/aircraft?edit=${aircraft.id}`)}
                        data-testid="aircraft-detail-mtom-missing"
                      >
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                          {t.fleet?.mtomMissing || 'MTOM missing'}
                          <span className="block text-xs font-normal">{t.fleet?.mtomMissingHint}</span>
                        </span>
                      </button>
                    ) : `${aircraft.mtom_kg} kg`}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Max. rychlost' : 'Max Speed'}</dt>
                  <dd className="font-medium">
                    {isMaxSpeedMissing(aircraft) ? (
                      <button
                        type="button"
                        className="text-left text-amber-600 hover:underline flex items-start gap-1"
                        title={t.fleet?.maxSpeedMissingHint}
                        onClick={() => navigate(`/aircraft?edit=${aircraft.id}`)}
                        data-testid="aircraft-detail-max-speed-missing"
                      >
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                          {t.fleet?.maxSpeedMissing || 'Max. speed missing'}
                          <span className="block text-xs font-normal">{t.fleet?.maxSpeedMissingHint}</span>
                        </span>
                      </button>
                    ) : `${aircraft.max_speed_ms} m/s`}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Maximální charakteristický rozměr' : 'Maximum Characteristic Dimension'}</dt>
                  <dd className="font-medium">{aircraft.max_characteristic_dimension_m != null ? `${aircraft.max_characteristic_dimension_m} m` : '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Verze firmware' : 'Firmware Version'}</dt>
                  <dd className="font-medium">{aircraft.firmware_version || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Bez označení C0-C6' : 'No C0-C6 Class Marking'}</dt>
                  <dd className="font-medium">{aircraft.legacy_drone ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">{language === 'cs' ? 'Vybavení SORA' : 'SORA Equipment'}</dt>
                  <dd className="font-medium">
                    {[
                      aircraft.equipment?.parachute && (language === 'cs' ? 'Padák' : 'Parachute'),
                      aircraft.equipment?.flight_termination_system && (language === 'cs' ? 'Systém ukončení letu (FTS)' : 'Flight Termination System'),
                      aircraft.equipment?.geo_awareness && 'Geo-awareness',
                      aircraft.equipment?.remote_id && 'Remote ID',
                    ].filter(Boolean).join(', ') || (language === 'cs' ? 'Žádné' : 'None')}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Maintenance Tab */}
        <TabsContent value="maintenance" data-testid="maintenance-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                {tr.title || 'Maintenance'}
              </CardTitle>
              {canManageMaintenance && maintenance.length > 0 && (
                <Button onClick={() => setSchedulerOpen(true)} data-testid="schedule-maintenance-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {tr.scheduleNew || 'Schedule Maintenance'}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {maintenance.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="space-y-6">
                  {/* Upcoming */}
                  {upcomingMaintenance.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {tr.upcoming || 'Upcoming'}
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{tr.dateRange || 'Period'}</TableHead>
                            <TableHead>{tr.typeHeader || 'Type'}</TableHead>
                            <TableHead>{tr.statusHeader || 'Status'}</TableHead>
                            <TableHead>{tr.reasonHeader || 'Reason'}</TableHead>
                            <TableHead>{tr.technicianHeader || 'Technician'}</TableHead>
                            <TableHead>{tr.actions || 'Actions'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {upcomingMaintenance.map(item => (
                            <MaintenanceRow key={item.id} item={item} />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {/* In Progress */}
                  {inProgressMaintenance.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-amber-600 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        {tr.inProgress || 'In Progress'}
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{tr.dateRange || 'Period'}</TableHead>
                            <TableHead>{tr.typeHeader || 'Type'}</TableHead>
                            <TableHead>{tr.statusHeader || 'Status'}</TableHead>
                            <TableHead>{tr.reasonHeader || 'Reason'}</TableHead>
                            <TableHead>{tr.technicianHeader || 'Technician'}</TableHead>
                            <TableHead>{tr.actions || 'Actions'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inProgressMaintenance.map(item => (
                            <MaintenanceRow key={item.id} item={item} />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {/* History */}
                  {historyMaintenance.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {tr.history || 'History'} ({language === 'cs' ? 'posledních 10' : 'last 10'})
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{tr.dateRange || 'Period'}</TableHead>
                            <TableHead>{tr.typeHeader || 'Type'}</TableHead>
                            <TableHead>{tr.statusHeader || 'Status'}</TableHead>
                            <TableHead>{tr.reasonHeader || 'Reason'}</TableHead>
                            <TableHead>{tr.technicianHeader || 'Technician'}</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {historyMaintenance.map(item => (
                            <MaintenanceRow key={item.id} item={item} />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Wave 3 Block 3A: Insurance Tab */}
        <TabsContent value="insurance" data-testid="insurance-tab-content">
          <AircraftInsuranceSection aircraftId={id} language={language} />
        </TabsContent>
      </Tabs>
      
      {/* Maintenance Scheduler Modal */}
      <MaintenanceScheduler
        isOpen={schedulerOpen}
        onClose={() => {
          setSchedulerOpen(false);
          setEditingMaintenance(null);
        }}
        aircraftId={id}
        maintenance={editingMaintenance}
        onSuccess={handleScheduleSuccess}
      />
      
      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => !open && setCancelDialog({ open: false, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              {tr.scheduler?.cancelMaintenance || 'Cancel Maintenance'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            {tr.scheduler?.confirmCancel || 'Are you sure you want to cancel this maintenance?'}
          </p>
          {cancelDialog.item && (
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-sm">
              <p><strong>{formatDateTime(cancelDialog.item.start_at)}</strong> - <strong>{formatDateTime(cancelDialog.item.end_at)}</strong></p>
              <p className="text-gray-500">{cancelDialog.item.reason}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog({ open: false, item: null })} disabled={cancelling}>
              {language === 'cs' ? 'Ne' : 'No'}
            </Button>
            <Button variant="destructive" onClick={handleConfirmCancel} disabled={cancelling}>
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'cs' ? 'Ano, zrušit' : 'Yes, cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AircraftDetailPage;
